import type { ObservationRow } from "../hooks/useObservationsCache";
import { supabase } from "../integrations/supabase/client";

// PostgREST caps a response at the project's max-rows (1000 by default), so full-history reads
// for CSV export/import page through the table instead of silently truncating at ~2.7 years.
const PAGE_SIZE = 1000;

const fetchPaged = async <T>(
  fetchPage: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>
): Promise<T[]> => {
  const all: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await fetchPage(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const page = data ?? [];
    all.push(...page);
    if (page.length < PAGE_SIZE) return all;
  }
};

/** Every observation for the owner, oldest first. */
export const fetchAllObservations = (ownerUserId: string): Promise<ObservationRow[]> =>
  fetchPaged((from, to) =>
    supabase
      .from("observations")
      .select("*")
      .eq("owner_user_id", ownerUserId)
      .order("observation_date", { ascending: true })
      .range(from, to)
  );

/** Dates (yyyy-MM-dd) that already have an observation within [from, to]. */
export const fetchObservationDates = async (
  ownerUserId: string,
  from: string,
  to: string
): Promise<Set<string>> => {
  const rows = await fetchPaged<{ observation_date: string }>((start, end) =>
    supabase
      .from("observations")
      .select("observation_date")
      .eq("owner_user_id", ownerUserId)
      .gte("observation_date", from)
      .lte("observation_date", to)
      .order("observation_date", { ascending: true })
      .range(start, end)
  );
  return new Set(rows.map((r) => r.observation_date));
};
